import type { ComputedRef, InjectionKey, MaybeRefOrGetter, Ref } from 'vue'
import { computed, inject, isRef, provide, ref, toValue } from 'vue'
import { useHead, useRoute, useSeoMeta } from 'nuxt/app'

import { INDEX_FOLLOW, NOINDEX_FOLLOW, NOINDEX_NOFOLLOW } from '../../types/seo'
import { resolveSeoHeadLinks, serializeSeoJsonLd, serializeSeoRobots } from '../../utils/seo-meta.utils'
import { absolutizeSeoJsonLd, resolveSeoAbsoluteUrl, resolveSeoCanonical } from '../../utils/seo.utils'
import { useLogger } from '../useLogger'
import { createFallbackSeoSiteProfile } from './seoSiteProfileFallback'
import type { ResolvedSeo, SeoDraft, SeoEntityInput, SeoSiteProfile } from '../../types/seo'

/**
 * `useSeo` — one SEO call per route.
 *
 * How the pieces fit together:
 *
 *   app root ──provideSeoSiteProfile(profile)──▶ [injection]
 *                                                    │
 *   page ──useSeo(draft)──▶ merge draft + profile ──▶ resolve URLs ──▶ head tags
 *
 * - **Site profile (per app):** brand-wide defaults — site name, base URL, default OG image,
 *   default robots, title formatter. Provided once at the app root.
 * - **Draft (per page):** what is specific to this route — title, description, canonical, image,
 *   robots, JSON-LD.
 *
 * The split exists so a page never repeats brand data, and so rebranding (or a new app adopting
 * `useSeo`) means changing one profile instead of every page.
 *
 * Related modules:
 * - `utils/seo.utils` — pure URL/path resolution
 * - `utils/seo-meta.utils` — pure robots / JSON-LD / head-link formatting
 * - `./seoSiteProfileFallback` — unbranded noindex safety net when the profile is missing
 */

export type {
  ResolvedSeo,
  SeoCanonical,
  SeoDraft,
  SeoEntityInput,
  SeoFactory,
  SeoHeadLink,
  SeoImage,
  SeoInput,
  SeoOpenGraphType,
  SeoPolicy,
  SeoPolicyDecision,
  SeoRobots,
  SeoSiteProfile,
} from '../../types/seo'

export { INDEX_FOLLOW, NOINDEX_FOLLOW, NOINDEX_NOFOLLOW }
export { createFallbackSeoSiteProfile }

/**
 * Provide/inject (not a store) because the profile is per app tree: the host app decides the
 * brand, and every page below it reads the same value without importing app-specific code into
 * core-layer.
 */
const SEO_PROFILE_KEY: InjectionKey<Ref<SeoSiteProfile>> = Symbol('seo-site-profile')

/**
 * Call once at the app root (GoKollab: `GKRootContainer.vue`).
 *
 * Accepts a ref/getter so a profile that depends on async data — e.g. Client Portal branding
 * loaded after boot — updates every page's tags without remounting.
 */
export function provideSeoSiteProfile(profile: MaybeRefOrGetter<SeoSiteProfile>): void {
  provide(
    SEO_PROFILE_KEY,
    computed(() => toValue(profile))
  )
}

/**
 * Read the profile provided by the app root.
 *
 * If the provide is missing we log and return brand-free noindex defaults instead of throwing, so
 * a misconfigured app still renders without reaching the index — see `seoSiteProfileFallback`.
 */
export function injectSeoSiteProfile(): Ref<SeoSiteProfile> {
  const profile = inject(SEO_PROFILE_KEY, null)
  if (profile) {
    return profile
  }

  const { logger } = useLogger()
  logger.error('[seo] No SeoSiteProfile provided. Call provideSeoSiteProfile() at the app root. Using unbranded noindex defaults.')
  return ref(createFallbackSeoSiteProfile())
}

function isEntityInput<TEntity>(input: MaybeRefOrGetter<SeoDraft> | SeoEntityInput<TEntity>): input is SeoEntityInput<TEntity> {
  return typeof input === 'object' && input !== null && !isRef(input) && 'source' in input && 'factory' in input
}

function hasMinimumMetadata(meta: ResolvedSeo): boolean {
  return Boolean(meta.title?.trim() && meta.description?.trim() && meta.image?.url)
}

/**
 * Reduce the caller's input to a plain draft.
 *
 * Entity input exists because indexability usually depends on the loaded entity (a thin listing
 * should not be indexed). While the entity is still loading we return an empty draft so the page
 * renders profile defaults rather than a half-filled entity title.
 */
function evaluateSeoInput<TEntity>(input: MaybeRefOrGetter<SeoDraft> | SeoEntityInput<TEntity>): { draft: SeoDraft; reason?: string } {
  if (!isEntityInput(input)) {
    return { draft: toValue<SeoDraft>(input) ?? {} }
  }

  const entity = toValue(input.source)
  if (!entity) return { draft: {} }

  const built = input.factory(entity)
  if (built.robots || !input.policy) return { draft: built }

  const decision = input.policy(entity)
  return { draft: { ...built, robots: decision.robots }, reason: decision.reason }
}

/** Write the resolved payload to the document head. Re-runs whenever `meta` changes. */
function renderSeoHead(meta: ComputedRef<ResolvedSeo>): void {
  useSeoMeta({
    title: () => meta.value.title,
    description: () => meta.value.description,
    ogTitle: () => meta.value.title,
    ogDescription: () => meta.value.description,
    ogType: () => meta.value.ogType,
    ogUrl: () => meta.value.canonicalUrl,
    ogSiteName: () => meta.value.siteName,
    ogImage: () => meta.value.image.url,
    ogImageAlt: () => meta.value.image.alt,
    twitterCard: () => meta.value.twitterCard,
    twitterTitle: () => meta.value.title,
    twitterDescription: () => meta.value.description,
    twitterImage: () => meta.value.image.url,
    twitterSite: () => meta.value.twitterSite,
    twitterCreator: () => meta.value.twitterCreator,
    robots: () => meta.value.robots,
  })

  useHead(() => ({
    // The profile's `formatTitle` produces the complete title, brand suffix included, and the same
    // string is reused for `og:title` / `twitter:title`. Clear any app-level `titleTemplate` (Client
    // Portal sets one in `useBrandedHead`) so it cannot append the brand a second time to `<title>`
    // and leave the social tags disagreeing with it.
    titleTemplate: null,
    link: [{ rel: 'canonical', href: meta.value.canonicalUrl }, ...meta.value.headLinks.map(link => ({ rel: link.rel, href: link.href }))],
    meta: import.meta.dev && meta.value.reason ? [{ name: 'x-seo-reason', content: meta.value.reason }] : [],
    script: meta.value.jsonLd ? [{ type: 'application/ld+json', innerHTML: serializeSeoJsonLd(meta.value.jsonLd) }] : [],
  }))
}

/**
 * Own the SEO tags for the current route.
 *
 * Call this from the **page** under `routing-layer/pages` (a composable may build the draft, but
 * the page makes the call — that is what the `routing-seo/require-page-use-seo` lint rule checks).
 */
export function useSeo(input: MaybeRefOrGetter<SeoDraft>): void
export function useSeo<TEntity>(input: SeoEntityInput<TEntity>): void
export function useSeo<TEntity>(input: MaybeRefOrGetter<SeoDraft> | SeoEntityInput<TEntity>): void {
  const profile = injectSeoSiteProfile()
  const route = useRoute()

  const evaluated = computed<{ draft: SeoDraft; reason?: string }>(() => evaluateSeoInput(input))

  const resolved = computed<ResolvedSeo>(() => {
    const { draft: page, reason } = evaluated.value
    const siteProfile = profile.value
    const robots = page.robots ?? siteProfile.defaultRobots
    const image = page.image ?? siteProfile.defaultImage

    const meta: ResolvedSeo = {
      title: siteProfile.formatTitle(page.title),
      description: page.description || siteProfile.defaultDescription,
      image: {
        ...image,
        // Preserve CDN/signed query params on OG/Twitter images only — not on page/entity URLs.
        url: resolveSeoAbsoluteUrl(image.url, siteProfile.baseUrl, { preserveQuery: true }),
      },
      canonicalUrl: resolveSeoCanonical(page.canonical, route.path, siteProfile.baseUrl),
      robots: serializeSeoRobots(robots),
      ogType: page.ogType ?? siteProfile.defaultOgType,
      siteName: siteProfile.siteName,
      twitterCard: siteProfile.defaultTwitterCard,
      twitterSite: siteProfile.twitterSite,
      twitterCreator: page.twitterCreator,
      jsonLd: page.jsonLd ? absolutizeSeoJsonLd(page.jsonLd, siteProfile.baseUrl) : undefined,
      headLinks: resolveSeoHeadLinks(page.headLinks, route.path, siteProfile.baseUrl),
      reason,
    }

    if (robots.index && !hasMinimumMetadata(meta) && import.meta.dev) {
      const { logger } = useLogger()
      logger.warn(`[seo] "${meta.canonicalUrl}" is index-eligible but missing title/description/image.`)
    }

    return meta
  })

  renderSeoHead(resolved)
}
